// Browser-compatible logger for React applications
// Replaces Winston with console-based logging

// Define log levels
const LogLevel = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  HTTP: 3,
  DEBUG: 4,
} as const;

type LogLevelType = typeof LogLevel[keyof typeof LogLevel];

// Get current log level from environment or default to INFO
const getCurrentLogLevel = (): LogLevelType => {
  if (process.env.NODE_ENV === 'development') {
    return LogLevel.DEBUG;
  }
  return LogLevel.INFO;
};

// Format timestamp
const formatTimestamp = (): string => {
  return new Date().toISOString().replace('T', ' ').slice(0, -5);
};

// Format log message
const formatMessage = (level: string, message: string): string => {
  return `${formatTimestamp()} [${level.toUpperCase()}]: ${message}`;
};

// Check if logging is enabled for the given level
const shouldLog = (level: LogLevelType): boolean => {
  return level <= getCurrentLogLevel();
};

// Logger implementation
const logger = {
  error: (message: string, error?: any) => {
    if (shouldLog(LogLevel.ERROR)) {
      console.error(formatMessage('error', message));
      if (error) {
        console.error(error);
      }
    }
  },

  warn: (message: string) => {
    if (shouldLog(LogLevel.WARN)) {
      console.warn(formatMessage('warn', message));
    }
  },

  info: (message: string) => {
    if (shouldLog(LogLevel.INFO)) {
      console.info(formatMessage('info', message));
    }
  },

  http: (message: string) => {
    if (shouldLog(LogLevel.HTTP)) {
      console.log(formatMessage('http', message));
    }
  },

  debug: (message: string) => {
    if (shouldLog(LogLevel.DEBUG)) {
      console.log(formatMessage('debug', message));
    }
  },
};

// Export the logger
export default logger;

// Export convenience methods (maintaining the same API as before)
export const logError = (message: string, error?: any) => {
  logger.error(message, error);
};

export const logWarn = (message: string) => {
  logger.warn(message);
};

export const logInfo = (message: string) => {
  logger.info(message);
};

export const logDebug = (message: string) => {
  logger.debug(message);
};

export const logHttp = (message: string) => {
  logger.http(message);
}; 